import type { Content } from "@tiptap/core";

import { suggestionContentToInlineNodes } from "./expand-suggestion";

/**
 * Builds the ProseMirror content array that inserts a template variable as a
 * mention pill followed by a trailing space.
 *
 * The `mention` node name is the tiptap default shared by both `CustomMention`
 * (plain `TiptapEditor`) and the email composer's `createEmailMention`, so the
 * same array inserts into either editor. This is the single insert definition
 * shared by the `@`-mention command path and the "Insert variable" modal.
 */
export function buildSuggestionMentionContent({
  id,
  label,
}: {
  id: string;
  label: string;
}): Content {
  return [
    { type: "mention", attrs: { id, label } },
    { type: "text", text: " " },
  ];
}

/**
 * Builds the content that a picked suggestion inserts into a plain
 * `TiptapEditor`.
 *
 * `expand` is the surface's answer to "is this text sent once, to a recipient
 * I already know?". One-off surfaces (a scribe note, a chat/SMS message) pass
 * `true` and get editable text, because there is nothing left to interpolate
 * later and the author should be able to correct the wording before sending.
 * Reusable surfaces (templates, sequences, assistant prompts) pass `false` and
 * keep the `@`-pill, because the token has to survive into storage and be
 * resolved per recipient at send time.
 *
 * Expanded content ends with a trailing space, matching the mention branch.
 * The caller deletes the space that followed the `@query` (mirroring tiptap's
 * own mention command), so content that added none would run straight into the
 * next word: `hello @first world` → `hello Jordanworld`.
 *
 * Returns an empty array when an expanded suggestion has no content —
 * ProseMirror rejects empty text nodes, so callers should treat that as
 * "insert nothing" and just clear the `@query` range.
 */
export function buildSuggestionInsertContent(
  item: { id: string; label: string; content?: string },
  { expand }: { expand: boolean },
): Content {
  if (!expand) {
    return buildSuggestionMentionContent(item);
  }
  const nodes = suggestionContentToInlineNodes(item.content ?? "");
  if (nodes.length === 0) {
    return [];
  }
  return [...nodes, { type: "text", text: " " }] as Content;
}
